import clsx from 'clsx'

export type TabItem<T extends string = string> = {
  id: T
  label: string
}

type TabsProps<T extends string> = {
  tabs: TabItem<T>[]
  activeTab: T
  onChange: (tab: T) => void
  className?: string
  /**
   * Classes aplicadas a cada `.nav-link`, e não à `<ul>`.
   *
   * Existe porque `className` não alcança o rótulo: `.nav-link` traz cor, tamanho e peso próprios, que
   * vencem o que for herdado do elemento pai. Uma tentativa de destacar as abas por `className` mediu
   * 2,33:1 no escuro e 2,22:1 no claro — os mesmos valores de antes da tentativa.
   *
   * É opt-in: sem estas duas props o componente renderiza exatamente o que sempre renderizou, e os outros
   * consumidores (valores de empresa e de profissional) não mudam.
   */
  tabClassName?: string
  /** Classes acrescentadas só à aba ativa, depois de `tabClassName`. */
  activeTabClassName?: string
}

export const CustomTabs = <T extends string>({
  tabs,
  activeTab,
  onChange,
  className,
  tabClassName,
  activeTabClassName,
}: TabsProps<T>) => {
  return (
    <ul
      className={clsx('nav nav-tabs nav-line-tabs custom-tabs', className)}
      style={{ borderBottom: 'none' }}
    >
      {tabs.map((tab) => (
        <li className='nav-item' key={tab.id}>
          <button
            type='button'
            // A aba ativa era sinalizada só pela classe `active` (cor/borda).
            // `aria-current` leva esse estado ao leitor de tela — cor não pode
            // ser o único portador de significado (WCAG 2.2 AA / BRAND.md).
            aria-current={activeTab === tab.id ? 'true' : undefined}
            className={clsx(
              'nav-link',
              tabClassName,
              { active: activeTab === tab.id },
              activeTab === tab.id && activeTabClassName
            )}
            onClick={() => onChange(tab.id)}
          >
            {tab.label}
          </button>
        </li>
      ))}
    </ul>
  )
}
